import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Search, Plus, Sparkles, CheckCircle2, Clock, Home, ArrowRight, ImageOff, Upload, Wand2, Check, Loader2, Sofa } from 'lucide-react';
import { mockSpecs, stagingStyles } from '../data/specsData';
import { initialModels } from '../data/optionsData';
export function SpecsPage() {
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModel, setFilterModel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedSpecs, setSelectedSpecs] = useState<Set<string>>(new Set());
  const filteredSpecs = useMemo(() => {
    return mockSpecs.filter(spec => {
      if (searchQuery && !spec.name.toLowerCase().includes(searchQuery.toLowerCase()) && !spec.modelName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filterModel !== 'all' && spec.modelId !== filterModel) {
        return false;
      }
      if (filterStatus !== 'all' && spec.status !== filterStatus) {
        return false;
      }
      return true;
    });
  }, [searchQuery, filterModel, filterStatus]);
  const stats = useMemo(() => {
    const total = mockSpecs.length;
    const published = mockSpecs.filter(s => s.status === 'published').length;
    const draft = mockSpecs.filter(s => s.status === 'draft').length;
    const uniqueModels = new Set(mockSpecs.map(s => s.modelId)).size;
    return {
      total,
      published,
      draft,
      uniqueModels
    };
  }, []);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-7541">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-7542">
        <div data-id="element-7543">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-7544">
            <Sparkles className="h-6 w-6 text-primary-500" data-id="element-7545" />
            Specs
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-7546">
            Pre-configured showroom packages for your models
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-7547">
          <Button variant="outline" leftIcon={<Upload className="h-4 w-4" data-id="element-7549" />} data-id="element-7548">
            Import CSV
          </Button>
          <Button leftIcon={<Plus className="h-4 w-4" data-id="element-7551" />} onClick={() => navigate(`/${builderId}/specs/new`)} data-id="element-7550">
            Create Spec
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7552">
        <Card className="p-4 flex items-center gap-4" data-id="element-7553">
          <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-7554">
            <Sparkles className="h-5 w-5 text-slate-600" data-id="element-7555" />
          </div>
          <div data-id="element-7556">
            <p className="text-sm font-medium text-slate-500" data-id="element-7557">Total Specs</p>
            <p className="text-2xl font-bold text-slate-900" data-id="element-7558">{stats.total}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-4" data-id="element-7559">
          <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center" data-id="element-7560">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" data-id="element-7561" />
          </div>
          <div data-id="element-7562">
            <p className="text-sm font-medium text-slate-500" data-id="element-7563">Published</p>
            <p className="text-2xl font-bold text-emerald-600" data-id="element-7564">
              {stats.published}
            </p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-4" data-id="element-7565">
          <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-7566">
            <Clock className="h-5 w-5 text-amber-600" data-id="element-7567" />
          </div>
          <div data-id="element-7568">
            <p className="text-sm font-medium text-slate-500" data-id="element-7569">Drafts</p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-7570">{stats.draft}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-4" data-id="element-7571">
          <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-7572">
            <Home className="h-5 w-5 text-blue-600" data-id="element-7573" />
          </div>
          <div data-id="element-7574">
            <p className="text-sm font-medium text-slate-500" data-id="element-7575">Models Covered</p>
            <p className="text-2xl font-bold text-blue-600" data-id="element-7576">
              {stats.uniqueModels}
            </p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4" data-id="element-7577">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-7578">
          <div className="relative flex-1" data-id="element-7579">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-7580" />
            <input type="text" placeholder="Search specs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow" data-id="element-7581" />
          </div>
          <select value={filterModel} onChange={e => setFilterModel(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500" data-id="element-7582">
            <option value="all" data-id="element-7583">All Models</option>
            {initialModels.map(m => <option key={m.id} value={m.id} data-id="element-7584">
                {m.name}
              </option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500" data-id="element-7585">
            <option value="all" data-id="element-7586">All Statuses</option>
            <option value="published" data-id="element-7587">Published</option>
            <option value="draft" data-id="element-7588">Draft</option>
          </select>
        </div>
      </Card>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7589">
        {filteredSpecs.map(spec => {
        const model = initialModels.find(m => m.id === spec.modelId);
        const isSelected = selectedSpecs.has(spec.id);
        return <Card key={spec.id} className={`overflow-hidden flex flex-col hover:shadow-md transition-shadow cursor-pointer group ${isSelected ? 'ring-2 ring-primary-500' : ''}`} onClick={() => navigate(`/${builderId}/specs/${spec.id}`)} data-id="element-7590">
              <div className="relative h-48 bg-slate-100 border-b border-slate-200" data-id="element-7591">
                {/* Bulk Select Checkbox */}
                <div className={`absolute top-3 left-3 z-10 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`} onClick={e => {
              e.stopPropagation();
              const next = new Set(selectedSpecs);
              if (next.has(spec.id)) next.delete(spec.id);else next.add(spec.id);
              setSelectedSpecs(next);
            }} data-id="element-7592">
                  <div className={`h-5 w-5 rounded border flex items-center justify-center ${isSelected ? 'bg-primary-500 border-primary-500 text-white' : 'bg-white border-slate-300 text-transparent hover:border-primary-500'}`} data-id="element-7593">
                    <Check className="h-3.5 w-3.5" data-id="element-7594" />
                  </div>
                </div>

                {/* Render Status Indicator */}
                {spec.renderStatus !== 'not-generated' && <div className="absolute top-3 left-10 z-10" data-id="element-7595">
                    {spec.renderStatus === 'ready' && <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/90 backdrop-blur-sm text-white text-[10px] font-medium shadow-sm" data-id="element-7596">
                        <div className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-7597"></div>
                        Assets Ready
                      </div>}
                    {spec.renderStatus === 'rendering' && <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/90 backdrop-blur-sm text-white text-[10px] font-medium shadow-sm" data-id="element-7598">
                        <div className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" data-id="element-7599"></div>
                        Rendering
                      </div>}
                    {spec.renderStatus === 'queued' && <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-600/90 backdrop-blur-sm text-white text-[10px] font-medium shadow-sm" data-id="element-7600">
                        <div className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-7601"></div>
                        Queued
                      </div>}
                  </div>}
                {model?.hasImage && model.imageUrl ? <img src={model.imageUrl} alt={model.name} className="w-full h-full object-cover" data-id="element-7602" /> : <div className="w-full h-full flex items-center justify-center text-slate-400" data-id="element-7603">
                    <ImageOff className="h-8 w-8" data-id="element-7604" />
                  </div>}
                <div className="absolute top-3 right-3" data-id="element-7605">
                  <Badge variant={spec.status === 'published' ? 'success' : 'neutral'} data-id="element-7606">
                    {spec.status === 'published' ? 'Published' : 'Draft'}
                  </Badge>
                </div>
              </div>

              <div className="p-5 flex flex-col flex-1" data-id="element-7607">
                <div className="mb-3" data-id="element-7608">
                  <div className="flex items-center justify-between mb-1" data-id="element-7609">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-7610">
                      {spec.modelName}
                    </p>
                    {spec.stagingStyleId && <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium" data-id="element-7611">
                        <Sofa className="h-3 w-3" data-id="element-7612" />
                        {stagingStyles.find(s => s.id === spec.stagingStyleId)?.name}
                      </div>}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-primary-600 transition-colors" data-id="element-7613">
                    {spec.name}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-1" data-id="element-7614">
                    {spec.description}
                  </p>
                </div>

                <div className="py-3 border-y border-slate-100 mb-4 flex-1" data-id="element-7615">
                  <p className="text-xs font-medium text-slate-500 mb-2" data-id="element-7616">
                    INCLUDED OPTIONS
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-7617">
                    {spec.selections.slice(0, 3).map((sel, i) => <span key={i} className="inline-flex items-center px-2 py-1 rounded bg-slate-100 text-[10px] font-medium text-slate-700 border border-slate-200" data-id="element-7618">
                        {sel.optionName}
                      </span>)}
                    {spec.selections.length > 3 && <span className="inline-flex items-center px-2 py-1 rounded bg-slate-50 text-[10px] font-medium text-slate-500 border border-slate-200" data-id="element-7619">
                        +{spec.selections.length - 3} more
                      </span>}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-auto" data-id="element-7620">
                  <div data-id="element-7621">
                    {spec.totalUpgradePrice > 0 ? <p className="text-sm font-semibold text-slate-900" data-id="element-7622">
                        +{formatCurrency(spec.totalUpgradePrice)}{' '}
                        <span className="text-xs font-normal text-slate-500" data-id="element-7623">
                          upgrades
                        </span>
                      </p> : <p className="text-sm font-medium text-slate-500" data-id="element-7624">
                        Standard (no upgrades)
                      </p>}
                  </div>
                  <span className="text-sm font-medium text-primary-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform" data-id="element-7625">
                    View Spec <ArrowRight className="h-4 w-4" data-id="element-7626" />
                  </span>
                </div>
              </div>
            </Card>;
      })}
      </div>

      {filteredSpecs.length === 0 && <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-id="element-7627">
          <Sparkles className="h-12 w-12 text-slate-300 mx-auto mb-3" data-id="element-7628" />
          <h3 className="text-lg font-medium text-slate-900" data-id="element-7629">No specs found</h3>
          <p className="text-slate-500 mt-1" data-id="element-7630">
            Try adjusting your search or filters.
          </p>
        </div>}

      {/* Floating Bulk Action Bar */}
      {selectedSpecs.size > 0 && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-200" data-id="element-7631">
          <div className="bg-slate-900 text-white rounded-xl shadow-2xl px-6 py-3 flex items-center gap-6 border border-slate-700" data-id="element-7632">
            <div className="flex items-center gap-4 border-r border-slate-700 pr-6" data-id="element-7633">
              <span className="font-medium" data-id="element-7634">
                {selectedSpecs.size} specs selected
              </span>
              <div className="flex items-center gap-3 text-sm" data-id="element-7635">
                <button onClick={() => setSelectedSpecs(new Set(filteredSpecs.map(s => s.id)))} className="text-slate-400 hover:text-white transition-colors" data-id="element-7636">
                  Select All
                </button>
                <button onClick={() => setSelectedSpecs(new Set())} className="text-slate-400 hover:text-white transition-colors" data-id="element-7637">
                  Clear
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-7638">
              <Button variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white" data-id="element-7639">
                Export Selected
              </Button>
              <Button className="bg-white text-slate-900 hover:bg-slate-100" leftIcon={<Wand2 className="h-4 w-4" data-id="element-7641" />} data-id="element-7640">
                Generate Marketing Packages
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}